import { useCallback, useEffect, useState } from 'react';

/**
 * "New" highlight for sidebar tabs. An item listed in `NEW_NAV_ITEM_IDS` is flagged until the
 * user opens it once; that is remembered per user in this browser.
 *
 * Plug-out: remove the id from `NEW_NAV_ITEM_IDS` (or empty the set).
 */
const NEW_NAV_ITEM_IDS: ReadonlySet<string> = new Set(['smart-finance']);

const storageKey = (userId: string) => `smartchg.seenNavItems.${userId}`;

function readSeen(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

export const useNewNavItems = (userId: string | null | undefined, currentItemId: string) => {
  const [seen, setSeen] = useState<Set<string>>(() => (userId ? readSeen(userId) : new Set()));

  useEffect(() => {
    setSeen(userId ? readSeen(userId) : new Set());
  }, [userId]);

  // Opening the tab once — from the sidebar, a link or a bookmark — retires its highlight.
  useEffect(() => {
    if (!userId || !NEW_NAV_ITEM_IDS.has(currentItemId) || seen.has(currentItemId)) return;
    const next = new Set(seen).add(currentItemId);
    setSeen(next);
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify([...next]));
    } catch {
      // Storage unavailable: the highlight simply shows again next visit.
    }
  }, [currentItemId, seen, userId]);

  return useCallback(
    (itemId: string) => Boolean(userId) && NEW_NAV_ITEM_IDS.has(itemId) && !seen.has(itemId),
    [seen, userId]
  );
};
